import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, ChevronDown, ChevronUp, Mail } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import toast from 'react-hot-toast';

const ROLES = ['user', 'collaborator', 'admin'];

function Admin() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [expandedUser, setExpandedUser] = useState(null);
  const [scanEmails, setScanEmails] = useState({});

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: rowsPerPage, offset: page * rowsPerPage });
      if (search) params.set('search', search);
      if (roleFilter) params.set('role', roleFilter);
      const res = await axios.get(`/admin/users?${params}`);
      setUsers(res.data.users); setTotal(res.data.pagination.total); setError(null);
    } catch (err) { setError(err.response?.data?.error || 'Failed to load users'); }
    finally { setLoading(false); }
  }, [page, rowsPerPage, search, roleFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.patch(`/admin/users/${userId}/role`, { role: newRole });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      toast.success('Role updated');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to update role'); }
  };

  const handleDelete = async () => {
    const u = deleteDialog.user; if (!u) return;
    try { await axios.delete(`/admin/users/${u.id}`); setDeleteDialog({ open: false, user: null }); toast.success('User deleted'); fetchUsers(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed to delete user'); }
  };

  const toggleScanEmails = async (userId) => {
    if (expandedUser === userId) { setExpandedUser(null); return; }
    setExpandedUser(userId);
    if (!scanEmails[userId]) {
      try { const res = await axios.get(`/admin/users/${userId}/scan-emails`); setScanEmails(prev => ({ ...prev, [userId]: res.data.emails })); }
      catch { toast.error('Failed to load scan emails'); }
    }
  };

  const handleDeleteScanEmail = async (userId, emailId) => {
    try { await axios.delete(`/admin/users/${userId}/scan-emails/${emailId}`); setScanEmails(prev => ({ ...prev, [userId]: prev[userId].filter(e => e.id !== emailId) })); toast.success('Scan email deleted'); }
    catch { toast.error('Failed to delete scan email'); }
  };

  const totalPages = Math.ceil(total / rowsPerPage);

  return (
    <>
      <Helmet><title>Admin - User Management</title></Helmet>
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-1">User Management</h2>
        <p className="text-gray-500">Manage users, roles, and scan email addresses</p>
      </div>

      {error && <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded mb-4">{error}</div>}

      <div className="bg-white rounded-xl shadow-sm border p-4 mb-4 flex flex-wrap gap-3">
        <input type="text" placeholder="Search by email or name..." value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-64 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
        <select value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setPage(0); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
          <option value="">All roles</option>
          {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        {loading ? (
          <div className="flex justify-center p-8"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="w-10 px-4 py-3" />
                    <th className="px-4 py-3 text-left font-semibold">Name</th>
                    <th className="px-4 py-3 text-left font-semibold">Email</th>
                    <th className="px-4 py-3 text-left font-semibold">Provider</th>
                    <th className="px-4 py-3 text-left font-semibold">Role</th>
                    <th className="px-4 py-3 text-left font-semibold">Joined</th>
                    <th className="px-4 py-3 text-left font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {users.map(u => (
                    <React.Fragment key={u.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <button onClick={() => toggleScanEmails(u.id)} className="text-gray-400 hover:text-gray-600">
                            {expandedUser === u.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="px-4 py-3">{u.name || '-'}</td>
                        <td className="px-4 py-3">{u.email}</td>
                        <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs font-medium border">{u.provider}</span></td>
                        <td className="px-4 py-3">
                          <select value={u.role || 'user'} onChange={e => handleRoleChange(u.id, e.target.value)}
                            className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </td>
                        <td className="px-4 py-3 text-gray-500">{new Date(u.created_at).toLocaleDateString()}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => setDeleteDialog({ open: true, user: u })} className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                      {expandedUser === u.id && (
                        <tr>
                          <td colSpan={7} className="px-8 py-3 bg-gray-50">
                            <div className="flex items-center gap-2 mb-2 text-sm font-semibold"><Mail className="w-4 h-4" /> Scan Email Addresses</div>
                            {scanEmails[u.id]?.length > 0 ? (
                              <ul className="space-y-1">
                                {scanEmails[u.id].map(e => (
                                  <li key={e.id} className="flex items-center justify-between text-sm">
                                    <div>
                                      <span className="font-mono">{e.scan_email}</span>
                                      <span className="ml-2 text-xs text-gray-400">{e.is_active ? 'Active' : 'Inactive'} | Created: {new Date(e.created_at).toLocaleDateString()}</span>
                                    </div>
                                    <button onClick={() => handleDeleteScanEmail(u.id, e.id)} className="text-red-500 hover:text-red-700 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                                  </li>
                                ))}
                              </ul>
                            ) : <p className="text-sm text-gray-400">No scan emails configured</p>}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t text-sm">
              <span className="text-gray-500">{total} users total</span>
              <div className="flex items-center gap-2">
                <select value={rowsPerPage} onChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }} className="border rounded px-2 py-1 text-sm">
                  {[10, 25, 50].map(n => <option key={n} value={n}>{n}/page</option>)}
                </select>
                <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-gray-50">Prev</button>
                <span>{page + 1} / {totalPages || 1}</span>
                <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-gray-50">Next</button>
              </div>
            </div>
          </>
        )}
      </div>

      {deleteDialog.open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-2">Delete User</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete <strong>{deleteDialog.user?.email}</strong>? This will also delete all their scan history and scan email addresses. This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteDialog({ open: false, user: null })} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Admin;
